import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  type TableState,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  onRowClick?: (rowValue: TData) => void;
}

export default function MobileListTable<TData, TValue>({
  columns,
  data,
  onRowClick = null,
  isLoading,
}: DataTableProps<TData, TValue>) {
  const is2xlDesktop = useTwBreakpoint("2xl");

  const [sortBy, setSortBy] = useQueryParam(
    "sortBy",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [sortOrder, setSortOrder] = useQueryParam(
    "sortOrder",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const sorting: SortingState = useMemo(() => {
    if (!sortBy || !sortOrder) return [];

    return [{ id: sortBy, desc: sortOrder === "desc" }];
  }, [sortBy, sortOrder]);

  const pageSize = is2xlDesktop ? 14 : 7;

  const state: Partial<TableState> = {
    sorting,
  };

  const reactTableOptions: TableOptions<TData> = {
    data: isLoading || !data ? new Array(pageSize).fill({}) : data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      if (newSorting.length === 0) {
        // Clear sorting
        setSortBy("");
        setSortOrder("");
      } else {
        // Set new sorting
        const sort = newSorting[0];
        setSortBy(sort.id);
        setSortOrder(sort.desc ? "desc" : "asc");
      }
    },
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false,
    state,
  };

  const { getHeaderGroups, getRowModel, getRowCount, firstPage } =
    useReactTable(reactTableOptions);

  if (getRowModel().rows.length === 0 && getRowCount() !== 0 && !isLoading) {
    firstPage();
  }

  return (
    <table className="w-full border-separate border-spacing-y-2.5 mb-auto -mt-2.5">
      <thead className="[&_tr]:h-7 bg-black-100 text-nowrap">
        {getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className="first:text-left first:rounded-l-3xl px-3 first:pr-0 last:text-right last:pl-0 last:rounded-r-3xl text-center text-xs text-black-700 align-middle"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {getRowModel().rows.map((row) => (
          <tr
            onClick={() => {
              if (onRowClick) {
                onRowClick(row.original);
              }
            }}
            key={row.id}
            data-state={row.getIsSelected() && "selected"}
            className="text-center text-xs rounded-3xl bg-white"
          >
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className="p-3 align-middle first:pr-0 first:text-left last:pl-0 last:text-right border-y border-black-200 text-sm first:rounded-l-3xl first:border-l last:rounded-r-3xl last:border-r"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
